import { cycleDayToday } from '../context';
import { addDays, fm, fr } from '../dates';
import { mean, r1 } from '../format';
import { bars, cycleLabel, cycleLegend } from './shared';

const FLOW_RANK = { light: 1, medium: 2, heavy: 3 };

/** "Heaviest flow day 2 · spotting Sep 27" for a period, from the raw logs. */
function latestPeriodNote(ctx, lp) {
  let heaviest = null;
  for (let d = lp.start_date; d <= lp.end_date && d <= ctx.today; d = addDays(d, 1)) {
    const flow = ctx.logs.period[d]?.flow;
    const rank = FLOW_RANK[String(flow).toLowerCase()];
    if (rank && (!heaviest || rank > heaviest.rank)) heaviest = { rank, flow, d };
  }
  const parts = [heaviest ? `Heaviest flow day ${ctx.info(heaviest.d)?.cd}` : 'No flow recorded'];
  const spotting = [];
  for (let d = ctx.cur.start; d <= ctx.today; d = addDays(d, 1)) {
    if (ctx.logs.period[d]?.spotting && !ctx.info(d)?.per) spotting.push(fm(d));
  }
  if (spotting.length) parts.push(`spotting ${spotting.join(', ')}`);
  return parts.join(' · ');
}

export function periodsScreen(ctx, x) {
  const cur = ctx.cur;
  const cyc = cur ? x.comp.concat([cur]) : x.comp;
  const lens = x.comp.map((c) => c.length).filter((v) => v != null);
  const avgLen = mean(lens);
  const prev = ctx.prev;
  const lp = ctx.latestPeriod;
  const cd = cycleDayToday(ctx);

  const kpis = [
    { label: 'Current cycle', value: cur ? `Day ${cd}` : '—', unit: '', scope: cur ? `Cycle ${cur.n} · ongoing since ${fm(cur.start)}` : 'No period logged yet', note: 'Not counted as a completed cycle' },
    { label: 'Latest completed cycle', value: prev ? String(prev.length) : '—', unit: prev ? 'days' : '', scope: prev ? `Cycle ${prev.n} · ${fr(prev.start, addDays(prev.nextStart, -1))}` : 'No completed cycle yet', note: '' },
    { label: 'Average cycle length', value: r1(avgLen), unit: avgLen == null ? '' : 'days', scope: `${x.rl} · completed only`, note: lens.length > 1 ? `Range ${Math.min(...lens)}–${Math.max(...lens)} days` : 'Pick 3 months or longer for a multi-cycle average' },
    lp
      ? { label: 'Latest period', value: String(lp.period_length), unit: 'days', scope: `${fr(lp.start_date, lp.end_date)} · last recorded start ${fm(lp.start_date)}`, note: latestPeriodNote(ctx, lp) }
      : { label: 'Latest period', value: '—', unit: '', scope: 'No period logged yet', note: '' },
  ];

  const maxLen = Math.max(35, ...lens, cd || 0);
  const hi = Math.ceil(maxLen / 5) * 5;
  const ticks = [];
  for (let t = 15; t <= hi; t += 5) ticks.push(t);
  const trend = {
    ...bars(
      cyc.map((c) => ({ ...cycleLabel(c), v: c.completed ? c.length : cd, txt: c.completed ? `${c.length}d` : `${cd}+ d`, ongoing: !c.completed })),
      15, hi, '#D49073', avgLen, `Avg ${r1(avgLen)}d`, ticks
    ),
    title: 'Cycle length by cycle',
    scope: `Days from one recorded period start to the next · ${x.rl}`,
    legend: cycleLegend('#D49073', x.rl),
    hasNote: Boolean(cur),
    note: cur ? `Cycle ${cur.n} is still ongoing — its bar shows days so far, not a length.` : '',
  };

  return { kpis, lists: [], trend, details: [] };
}
